import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Card,
  CardBody,
  CardHeader,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useToast,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Badge,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
  Center,
  Code,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  FormErrorMessage,
  Switch
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';

const EditABTest = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    entity_type: 'component',
    entity_id: '',
    goal_metric: 'conversion_rate',
    traffic_split: 50,
    variant_a_data: {},
    variant_b_data: {},
    status: 'draft'
  });
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const toast = useToast();
  const navigate = useNavigate();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    if (id && tenantId) {
      fetchTestData();
    }
  }, [id, tenantId]);

  useEffect(() => {
    if (formData.entity_type && tenantId) {
      fetchEntities();
    }
  }, [formData.entity_type, tenantId]);

  const fetchTestData = async () => {
    try {
      setLoading(true);
      console.log('Fetching test data for ID:', id);
      
      const response = await apiClient(`/ab-tests/${tenantId}/${id}`);
      console.log('Test data response:', response);
      
      let testData = null;
      
      // Handle different response structures
      if (response && response.success && response.data) {
        testData = response.data;
      } else if (response && response.status === 'success' && response.data) {
        testData = response.data;
      } else if (response && response.id) {
        testData = response;
      } else {
        throw new Error('Test not found');
      }
      
      if (testData) {
        setFormData({
          name: testData.name || '',
          description: testData.description || '',
          entity_type: testData.entity_type || 'component',
          entity_id: testData.entity_id || '',
          goal_metric: testData.goal_metric || 'conversion_rate',
          traffic_split: testData.traffic_split || 50,
          variant_a_data: typeof testData.variant_a_data === 'string' 
            ? JSON.parse(testData.variant_a_data || '{}')
            : testData.variant_a_data || {},
          variant_b_data: typeof testData.variant_b_data === 'string'
            ? JSON.parse(testData.variant_b_data || '{}') 
            : testData.variant_b_data || {},
          status: testData.status || 'draft'
        });
      }
    } catch (error) {
      console.error('Error fetching test data:', error);
      toast({
        title: 'Error loading test',
        description: error.message || 'Failed to load test data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      navigate('/ab-testing');
    } finally {
      setLoading(false);
    }
  };

  const fetchEntities = async () => {
    try {
      let endpoint = '';
      switch (formData.entity_type) {
        case 'component':
        case 'page_component':
          endpoint = `/page-components/${tenantId}`;
          break;
        case 'content_entry':
          endpoint = `/content-entries/${tenantId}`;
          break;
        case 'component_layout':
          endpoint = `/component-layouts/${tenantId}`;
          break;
        case 'navigation':
          endpoint = `/navigations/${tenantId}`;
          break;
        case 'page':
          endpoint = `/pages/${tenantId}`;
          break;
        default:
          return;
      }
      
      console.log('Fetching entities from:', endpoint);
      const response = await apiClient(endpoint);
      console.log('Entities response:', response);
      
      let entitiesData = [];
      
      // Handle different response formats
      if (response && response.success && Array.isArray(response.data)) {
        // Format: { success: true, data: [...] }
        entitiesData = response.data;
      } else if (response && response.status === 'success' && Array.isArray(response.data)) {
        // Format: { status: "success", data: [...] } - for content entries
        entitiesData = response.data;
      } else if (Array.isArray(response)) {
        // Direct array response
        entitiesData = response;
        
        // Handle nested array structure for page components
        if (entitiesData.length > 0 && Array.isArray(entitiesData[0])) {
          entitiesData = entitiesData.flat();
        }
      } else if (response && response.data && Array.isArray(response.data)) {
        // Fallback: if there's a data property
        entitiesData = response.data;
        
        // Handle nested array structure
        if (entitiesData.length > 0 && Array.isArray(entitiesData[0])) {
          entitiesData = entitiesData.flat();
        }
      } else {
        // Handle page components nested structure
        const possibleArrays = Object.values(response || {}).filter(value => Array.isArray(value));
        if (possibleArrays.length > 0) {
          entitiesData = possibleArrays.flat();
        }
      }
      
      // Filter out any invalid entries and ensure we have required properties
      entitiesData = entitiesData.filter(entity => 
        entity && 
        typeof entity === 'object' && 
        entity.id && 
        (entity.name || entity.title || entity.default_title || entity.identifier)
      );
      
      console.log('Processed entities:', entitiesData);
      setEntities(entitiesData);
      
    } catch (error) {
      console.error(`Error fetching ${formData.entity_type}s:`, error);
      toast({
        title: `Error loading ${formData.entity_type}s`,
        description: error.message || `Failed to load ${formData.entity_type}s`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setEntities([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.entity_type || !formData.entity_id) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setSaving(true);

    try {
      const payload = {
        ...formData,
        variant_a_data: JSON.stringify(formData.variant_a_data),
        variant_b_data: JSON.stringify(formData.variant_b_data)
      };

      console.log('Updating A/B test with payload:', payload);
      const response = await apiClient(`/ab-tests/${tenantId}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      console.log('Update A/B test response:', response);

      // Handle different success response formats
      if (response && (response.success || response.status === 'success' || response.id || response.message)) {
        toast({
          title: 'A/B test updated',
          description: response.message || 'Your A/B test has been updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        navigate('/ab-testing');
      } else {
        throw new Error(response?.message || response?.error || 'Failed to update A/B test');
      }
    } catch (error) {
      console.error('Error updating A/B test:', error);
      toast({
        title: 'Error updating A/B test',
        description: error.message || 'Failed to update A/B test',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const getEntityLabel = (entityType) => {
    switch (entityType) {
      case 'page_component': return 'Page Component'; // Best for page content testing
      case 'content_entry': return 'Content Entry';   // Best for content/copy testing
      case 'component_layout': return 'Component Layout'; // For layout testing
      case 'navigation': return 'Navigation';
      case 'page': return 'Page';
      default: return entityType;
    }
  };

  // Update the getEntityName function to handle all entity types
  const getEntityName = (entity) => {
    switch (formData.entity_type) {
      case 'page_component':
      case 'component':
        return entity.name || entity.identifier || `Component #${entity.id}`;
      case 'content_entry':
        return entity.default_title || 
               entity.title || 
               (entity.translations && entity.translations[0] && entity.translations[0].title) ||
               `Content Entry #${entity.id}`;
      case 'component_layout':
        return entity.name || `Layout #${entity.id}`;
      case 'navigation':
        return entity.name || `Navigation #${entity.id}`;
      case 'page':
        return entity.name || entity.title || `Page #${entity.id}`;
      default:
        return entity.name || entity.title || entity.identifier || `${formData.entity_type} #${entity.id}`;
    }
  };

  const canEdit = formData.status === 'draft' || formData.status === 'paused';

  if (loading) {
    return (
      <Container maxW="container.lg" py={6}>
        <Center py={20}>
          <VStack spacing={4}>
            <Spinner size="xl" />
            <Text>Loading test data...</Text>
          </VStack>
        </Center>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" py={6}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/ab-testing">A/B Testing</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Edit Test</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <form onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Box>
              <HStack spacing={3} mb={2}>
                <Heading size="lg">Edit A/B Test</Heading>
                <Badge colorScheme={
                  formData.status === 'running' ? 'green' :
                  formData.status === 'completed' ? 'blue' :
                  formData.status === 'paused' ? 'orange' : 'gray'
                }>
                  {formData.status}
                </Badge>
              </HStack>
              <Text color="gray.500">
                Update your A/B test configuration
              </Text>
            </Box>
            <HStack>
              <Button variant="outline" onClick={() => navigate('/ab-testing')}>
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={saving}
                loadingText="Saving..."
                isDisabled={!canEdit}
              >
                Save Changes
              </Button>
            </HStack>
          </HStack>

          {!canEdit && (
            <Alert status="warning">
              <AlertIcon />
              <AlertDescription>
                This test is currently {formData.status}. Only draft and paused tests can be edited.
              </AlertDescription>
            </Alert>
          )}

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardHeader>
              <Heading size="md">Test Configuration</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Test Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Homepage Hero Button Test"
                    isDisabled={!canEdit}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Testing different button colors to improve conversion rate"
                    rows={3}
                    isDisabled={!canEdit}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Test Type</FormLabel>
                  <Select
                    value={formData.entity_type}
                    onChange={(e) => setFormData({ ...formData, entity_type: e.target.value, entity_id: '' })}
                    isDisabled={!canEdit}
                  >
                    <option value="page">Page</option>
                    <option value="page_component">Page Component</option>
                    <option value="content_entry">Content Entry</option>
                    <option value="component_layout">Component Layout</option>
                    <option value="navigation">Navigation</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>{getEntityLabel(formData.entity_type)} to Test</FormLabel>
                  <Select
                    value={formData.entity_id}
                    onChange={(e) => setFormData({ ...formData, entity_id: e.target.value })}
                    placeholder={`Select a ${formData.entity_type}`}
                    isDisabled={!canEdit}
                  >
                    {entities.map((entity) => (
                      <option key={entity.id} value={entity.id}>
                        {getEntityName(entity)}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Goal Metric</FormLabel>
                  <Select
                    value={formData.goal_metric}
                    onChange={(e) => setFormData({ ...formData, goal_metric: e.target.value })}
                    isDisabled={!canEdit}
                  >
                    <option value="conversion_rate">Conversion Rate</option>
                    <option value="click_through_rate">Click Through Rate</option>
                    <option value="bounce_rate">Bounce Rate</option>
                    <option value="time_on_page">Time on Page</option>
                    <option value="revenue_per_visitor">Revenue per Visitor</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Traffic Split</FormLabel>
                  <HStack>
                    <Box flex={1}>
                      <Text fontSize="sm" mb={2}>Variant A: {formData.traffic_split}%</Text>
                      <NumberInput
                        value={formData.traffic_split}
                        onChange={(_, value) => setFormData({ ...formData, traffic_split: value })}
                        min={10}
                        max={90}
                        step={5}
                        isDisabled={!canEdit}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </Box>
                    <Box flex={1}>
                      <Text fontSize="sm" mb={2}>Variant B: {100 - formData.traffic_split}%</Text>
                      <Input value={`${100 - formData.traffic_split}%`} isReadOnly />
                    </Box>
                  </HStack>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardHeader>
              <Heading size="md">Variant Configuration</Heading>
            </CardHeader>
            <CardBody>
              <Tabs>
                <TabList>
                  <Tab>Variant A (Control)</Tab>
                  <Tab>Variant B (Treatment)</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <VStack spacing={4} align="stretch">
                      <Text color="gray.500">
                        Configure the control version of your test
                      </Text>
                      
                      {/* Add editable fields for Variant A */}
                      <FormControl>
                        <FormLabel>Variant A Configuration</FormLabel>
                        <Textarea
                          value={JSON.stringify(formData.variant_a_data, null, 2)}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              setFormData({ ...formData, variant_a_data: parsed });
                            } catch (error) {
                              // Keep the text but show error state
                              console.warn('Invalid JSON for Variant A');
                            }
                          }}
                          placeholder="Enter JSON configuration for Variant A"
                          rows={8}
                          fontFamily="mono"
                          isDisabled={!canEdit}
                        />
                        <Text fontSize="sm" color="gray.500" mt={1}>
                          Enter valid JSON configuration for the control variant
                        </Text>
                      </FormControl>
                      
                      {/* Quick configuration options for common use cases */}
                      <Box>
                        <Text fontWeight="medium" mb={2}>Quick Configuration:</Text>
                        <VStack spacing={2} align="stretch">
                          <HStack>
                            <Text fontSize="sm">Button Color:</Text>
                            <Input
                              size="sm"
                              placeholder="e.g., #0066cc"
                              value={formData.variant_a_data?.buttonColor || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                variant_a_data: { ...formData.variant_a_data, buttonColor: e.target.value }
                              })}
                              isDisabled={!canEdit}
                            />
                          </HStack>
                          <HStack>
                            <Text fontSize="sm">Button Text:</Text>
                            <Input
                              size="sm"
                              placeholder="e.g., Get Started"
                              value={formData.variant_a_data?.buttonText || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                variant_a_data: { ...formData.variant_a_data, buttonText: e.target.value }
                              })}
                              isDisabled={!canEdit}
                            />
                          </HStack>
                          <HStack>
                            <Text fontSize="sm">Headline:</Text>
                            <Input
                              size="sm"
                              placeholder="e.g., Welcome to Our Service"
                              value={formData.variant_a_data?.headline || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                variant_a_data: { ...formData.variant_a_data, headline: e.target.value }
                              })}
                              isDisabled={!canEdit}
                            />
                          </HStack>
                        </VStack>
                      </Box>
                    </VStack>
                  </TabPanel>
                  
                  <TabPanel>
                    <VStack spacing={4} align="stretch">
                      <Text color="gray.500">
                        Configure the treatment version of your test
                      </Text>
                      
                      {/* Add editable fields for Variant B */}
                      <FormControl>
                        <FormLabel>Variant B Configuration</FormLabel>
                        <Textarea
                          value={JSON.stringify(formData.variant_b_data, null, 2)}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              setFormData({ ...formData, variant_b_data: parsed });
                            } catch (error) {
                              // Keep the text but show error state
                              console.warn('Invalid JSON for Variant B');
                            }
                          }}
                          placeholder="Enter JSON configuration for Variant B"
                          rows={8}
                          fontFamily="mono"
                          isDisabled={!canEdit}
                        />
                        <Text fontSize="sm" color="gray.500" mt={1}>
                          Enter valid JSON configuration for the treatment variant
                        </Text>
                      </FormControl>
                      
                      {/* Quick configuration options for common use cases */}
                      <Box>
                        <Text fontWeight="medium" mb={2}>Quick Configuration:</Text>
                        <VStack spacing={2} align="stretch">
                          <HStack>
                            <Text fontSize="sm">Button Color:</Text>
                            <Input
                              size="sm"
                              placeholder="e.g., #ff6600"
                              value={formData.variant_b_data?.buttonColor || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                variant_b_data: { ...formData.variant_b_data, buttonColor: e.target.value }
                              })}
                              isDisabled={!canEdit}
                            />
                          </HStack>
                          <HStack>
                            <Text fontSize="sm">Button Text:</Text>
                            <Input
                              size="sm"
                              placeholder="e.g., Start Now"
                              value={formData.variant_b_data?.buttonText || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                variant_b_data: { ...formData.variant_b_data, buttonText: e.target.value }
                              })}
                              isDisabled={!canEdit}
                            />
                          </HStack>
                          <HStack>
                            <Text fontSize="sm">Headline:</Text>
                            <Input
                              size="sm"
                              placeholder="e.g., Transform Your Business Today"
                              value={formData.variant_b_data?.headline || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                variant_b_data: { ...formData.variant_b_data, headline: e.target.value }
                              })}
                              isDisabled={!canEdit}
                            />
                          </HStack>
                        </VStack>
                      </Box>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </CardBody>
          </Card>
        </VStack>
      </form>
    </Container>
  );
};

export default EditABTest;